/*
  Live-Vorschau für den Schichtplan-Export.

  PDF: hier steht das erzeugte Dokument selbst, eingebettet als Objekt-URL. Ein
  HTML-Nachbau lief zwangsläufig auseinander (Logo, Fusszeile, Schriften,
  Spaltenbreiten) – die Vorschau soll aber zeigen, was heruntergeladen wird.

  Excel: dort geht das nicht, eine .xlsx lässt sich im Browser nicht anzeigen.
  Das Sheet bleibt darum nachgebaut.
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import AppLoadingOverlay from "../../../../design-system/components/AppLoadingOverlay";
import { buildEntryPalette, getReadableTextColor, mixHexColors } from "./exportColors";
import { EXPORT_FORMATS } from "./exportFormats";
import { createShiftplanPdfObjectUrl } from "./pdf/buildShiftplanPdf";

// Tippen im Fusstext soll nicht jede Taste lang ein PDF bauen.
const PREVIEW_DEBOUNCE_MS = 220;
const PREVIEW_STATUS = Object.freeze({ LOADING: "loading", READY: "ready", ERROR: "error" });

/*
  Das erzeugte PDF im Fenster. Neu gebaut wird es verzögert, damit ein
  Tastendruck im Fusstext nicht jedes Mal ein Dokument erzeugt; die alte
  Objekt-URL wird erst freigegeben, wenn die neue steht – sonst blitzt das
  Fenster zwischendurch leer auf.
*/
function PdfLivePreview({ model, settings, periodLabel, companyName }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [status, setStatus] = useState(PREVIEW_STATUS.LOADING);
  const activeUrlRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    setStatus(PREVIEW_STATUS.LOADING);

    const handle = window.setTimeout(() => {
      createShiftplanPdfObjectUrl({ model, settings, periodLabel, companyName })
        .then((nextUrl) => {
          if (cancelled) {
            URL.revokeObjectURL(nextUrl);
            return;
          }

          if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
          activeUrlRef.current = nextUrl;
          setPreviewUrl(nextUrl);
          setStatus(PREVIEW_STATUS.READY);
        })
        .catch(() => {
          if (!cancelled) setStatus(PREVIEW_STATUS.ERROR);
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [companyName, model, periodLabel, settings]);

  useEffect(
    () => () => {
      if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
      activeUrlRef.current = "";
    },
    []
  );

  const hasDocument = Boolean(previewUrl) && status !== PREVIEW_STATUS.ERROR;

  return (
    // Die Fläche trägt das Seitenverhältnis des Blatts, damit das Dokument sie
    // ausfüllt – sonst steht rundherum der dunkle Grund des PDF-Betrachters.
    <Box
      className={`spv2-export-pdf-preview${settings.orientation === "portrait" ? " is-portrait" : ""}`}
    >
      {hasDocument ? (
        <iframe
          key={previewUrl}
          className="spv2-export-pdf-frame"
          title="PDF-Vorschau"
          src={`${previewUrl}#toolbar=0&navpanes=0&statusbar=0&view=FitH`}
        />
      ) : null}

      {/* Passiv: während neu gebaut wird, bleibt das bisherige Blatt bedienbar. */}
      <AppLoadingOverlay
        open={status === PREVIEW_STATUS.LOADING}
        scope="contained"
        blocking={false}
        label="Vorschau wird erstellt"
      />

      {status === PREVIEW_STATUS.ERROR ? (
        <span className="spv2-export-pdf-status is-error" role="alert">
          Die Vorschau lässt sich hier nicht anzeigen – der Download ist davon nicht betroffen.
        </span>
      ) : null}
    </Box>
  );
}

function ExcelCellContent({ entry }) {
  if (entry.time) {
    return (
      <span className={`spv2-export-excel-cell-line${entry.isDraft ? " is-draft" : ""}`}>
        <span className="spv2-export-excel-cell-time">{entry.time}</span>
        {entry.name ? <span className="spv2-export-excel-cell-name">{entry.name}</span> : null}
        {entry.isDraft ? <span className="spv2-export-excel-cell-draft">Entwurf</span> : null}
      </span>
    );
  }

  return (
    <span className={`spv2-export-excel-cell-line${entry.isDraft ? " is-draft" : ""}`}>
      <span className="spv2-export-excel-cell-time">{entry.name || entry.fullLabel}</span>
      {entry.isDraft ? <span className="spv2-export-excel-cell-draft">Entwurf</span> : null}
    </span>
  );
}

function ExcelPreview({
  model,
  settings,
  periodLabel,
  sections,
  activeSectionId,
  onSectionChange,
}) {
  const monochrome = settings.colorMode === "mono";
  const metaParts = [
    `${model.rows.length} Mitarbeitende`,
    `${model.stats.shiftCount} Schichten`,
    settings.includeAbsences ? `${model.stats.absenceCount} Absenzen` : "",
    settings.includeHolidays ? `${model.stats.holidayCount} Ferien` : "",
    model.stats.draftCount > 0 ? `${model.stats.draftCount} Entwürfe` : "",
  ].filter(Boolean);
  const legend = settings.includeLegend ? model.legend : [];

  return (
    <Box className="spv2-export-excel-preview" aria-label="Excel-Vorschau">
      <Box className="spv2-export-excel-scroll">
        <Box className="spv2-export-excel-titleblock">
          <span className="spv2-export-excel-title">{`Schichtplan · ${periodLabel}`}</span>
          <span className="spv2-export-excel-meta">{metaParts.join("      ·      ")}</span>
        </Box>
        <table className="spv2-export-excel-table">
          <thead>
            <tr>
              <th className="spv2-export-excel-namehead">Mitarbeitende</th>
              {model.days.map((day) => (
                <th
                  key={day.key}
                  className={`${day.isWeekend ? "is-weekend" : ""}${day.publicHolidayNames.length ? " is-holiday" : ""}`}
                  title={day.publicHolidayNames.join(", ")}
                >
                  <span className="spv2-export-excel-head-weekday">{day.weekdayShort.replace(".", "")}</span>
                  <span className="spv2-export-excel-head-date">{day.dayMonthLabel}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {model.rows.map((row) => (
              <tr key={row.id}>
                <td className="spv2-export-excel-namecol">{row.name}</td>
                {model.days.map((day) => {
                  const entries = row.cellsByDayKey[day.key] || [];
                  if (!entries.length) {
                    return <td key={day.key} className={day.isWeekend ? "is-weekend" : ""} />;
                  }

                  const palette = buildEntryPalette(entries[0].color, { monochrome });
                  const fillColor = entries.length === 1 ? palette.body : mixHexColors(palette.body, "#FFFFFF", 0.4);
                  const textColor = getReadableTextColor(fillColor);

                  return (
                    <td key={day.key} style={{ backgroundColor: fillColor, color: textColor }}>
                      {entries.map((entry) => (
                        <ExcelCellContent key={`${entry.kind}:${entry.entryId}`} entry={entry} />
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {legend.length ? (
          <Box className="spv2-export-excel-legend">
            <span className="spv2-export-excel-legend-title">Legende</span>
            <Box className="spv2-export-excel-legend-items">
              {legend.map((item) => {
                const palette = buildEntryPalette(item.color, { monochrome });
                const textColor = getReadableTextColor(palette.body);
                return (
                  <span
                    key={item.key}
                    className="spv2-export-excel-legend-item"
                    style={{ backgroundColor: palette.body, color: textColor }}
                  >
                    {item.timeLabel ? `${item.name} · ${item.timeLabel}` : item.name}
                    <span className="spv2-export-excel-legend-count">{item.count}×</span>
                  </span>
                );
              })}
            </Box>
          </Box>
        ) : null}
      </Box>
      <Box className="spv2-export-excel-tabs" aria-label="Excel-Arbeitsblätter">
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            className={`spv2-export-excel-tab${
              section.id === activeSectionId ? " is-active" : ""
            }`}
            onClick={() => onSectionChange(section.id)}
          >
            {section.title}
          </button>
        ))}
      </Box>
    </Box>
  );
}

export default function ExportPreview({ format, model, sections, settings, periodLabel, companyName = "" }) {
  const exportSections = useMemo(
    () =>
      Array.isArray(sections) && sections.length
        ? sections
        : [{ id: "default", title: "Schichtplan", model }],
    [model, sections]
  );
  const [activeSectionId, setActiveSectionId] = useState(exportSections[0]?.id || "");

  useEffect(() => {
    if (!exportSections.some((section) => section.id === activeSectionId)) {
      setActiveSectionId(exportSections[0]?.id || "");
    }
  }, [activeSectionId, exportSections]);

  const activeSection =
    exportSections.find((section) => section.id === activeSectionId) ||
    exportSections[0];

  return (
    <Box className="spv2-export-preview-canvas">
      {format === EXPORT_FORMATS.EXCEL ? (
        <ExcelPreview
          model={activeSection.model}
          settings={settings}
          periodLabel={periodLabel}
          sections={exportSections}
          activeSectionId={activeSection.id}
          onSectionChange={setActiveSectionId}
        />
      ) : (
        <PdfLivePreview model={model} settings={settings} periodLabel={periodLabel} companyName={companyName} />
      )}
    </Box>
  );
}
